import React from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@components/MetricCard';
import { CallPerformanceMetrics } from '../types';
import { getTrendType } from '../utils/trendUtils';
import { formatTrend } from '../utils/analyticsUtils';

interface CallPerformanceMetricsProps {
  metrics: CallPerformanceMetrics | null;
}

export const CallPerformanceMetricsCards: React.FC<CallPerformanceMetricsProps> = ({ metrics }) => {
  const { t } = useTranslation();
  if (!metrics) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {t('analytics.callPerformance')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <MetricCard key={i} title={t('common.loading')} value="" loading={true} />
          ))}
        </div>
      </div>
    );
  }

  const metricCards = [
    {
      title: t('analytics.totalCalls'),
      value: metrics.total_calls.value,
      icon: 'phone',
      variant: 'primary' as const,
      trend: formatTrend(metrics.total_calls.trend),
      trendType: getTrendType(metrics.total_calls.trend_direction),
    },
    {
      title: t('analytics.answeredRate'),
      value: `${metrics.answered_rate.value}%`,
      icon: 'check_circle',
      variant: 'success' as const,
      trend: formatTrend(metrics.answered_rate.trend),
      trendType: getTrendType(metrics.answered_rate.trend_direction),
    },
    {
      title: t('analytics.avgDuration'),
      value: metrics.avg_duration.value,
      icon: 'schedule',
      variant: 'info' as const,
      trend: formatTrend(metrics.avg_duration.trend),
      trendType: getTrendType(metrics.avg_duration.trend_direction),
    },
    {
      title: t('analytics.totalMinutes'),
      value: metrics.total_minutes.value,
      icon: 'timer',
      variant: 'warning' as const,
      trend: formatTrend(metrics.total_minutes.trend),
      trendType: getTrendType(metrics.total_minutes.trend_direction),
    },
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        {t('analytics.callPerformance')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((card, index) => (
          <MetricCard
            key={index}
            title={card.title}
            value={card.value}
            icon={card.icon}
            variant={card.variant}
            trend={card.trend}
            trendType={card.trendType}
          />
        ))}
      </div>
    </div>
  );
};
